/**
 * The back office's order API, typed at the edge of this package.
 *
 * The payloads here are **larger** than the customer's: the prepared
 * quantities, the hold reason and the synchronisation state are on them,
 * because the order desk needs all three and a customer must see none of them.
 * That the two are different types is the point — a column added here cannot
 * reach a customer's screen.
 */

import type { ApiClient } from "@hardauto/core-api";

const BASE = "/api/v1";

/**
 * The three `quote_*` states belong to a quotation and never to an order, and
 * the reverse holds too — the server keeps them apart with a check
 * constraint, so a screen may branch on `kind` and trust it.
 */
export type OrderStatus =
  | "pending_approval"
  | "confirmed"
  | "preparing"
  | "shipped"
  | "delivered"
  | "cancelled"
  | "quote_requested"
  | "quote_sent"
  | "quote_accepted";

/** A document is an order, or a quotation for one. */
export type OrderKind = "order" | "quote";

export type LineStatus = "ordered" | "prepared" | "partially_shipped" | "shipped" | "cancelled";

export interface Delivery {
  site_code: string;
  address: string;
  city: string;
  contact_name: string;
  contact_phone: string;
  customer_reference: string;
  comment: string;
}

export interface OrderLine {
  id: string;
  article_id: string;
  reference: string;
  designation: string;
  sales_unit: string;
  site_code: string;
  quantity: string;
  unit_price: string;
  discount_pct: string;
  amount: string;
  quantity_shipped: string;
  outstanding: string;
  status: LineStatus;
}

export interface BackOfficeLine extends OrderLine {
  quantity_prepared: string;
  is_cancelled: boolean;
}

export interface Order {
  id: string;
  reference: string;
  status: OrderStatus;
  delivery: Delivery;
  lines: OrderLine[];
  subtotal: string;
  tax_rate_pct: string;
  tax_amount: string;
  shipping_amount: string;
  total: string;
  payment_terms: string;
  placed_at: string | null;
  is_open: boolean;
  kind: OrderKind;
  /** When a quotation stops standing. `null` on an order. */
  valid_until: string | null;
  /** The order this quotation produced, once the customer accepted it. */
  converted_order_id: string | null;
  /** The quotation this order came from, when it came from one. */
  quote_id: string | null;
}

export interface OrderList {
  items: Order[];
  total: number;
  next_cursor: string | null;
}

export interface Shipment {
  id: string;
  reference: string;
  site_code: string;
  carrier: string;
  tracking_reference: string;
  shipped_at: string | null;
  lines: { order_line_id: string; quantity: string }[];
}

export interface BackOfficeOrder {
  order: Order;
  lines: BackOfficeLine[];
  shipments: Shipment[];
  client_id: string;
  /** Why it is waiting, when it is. `credit_limit_exceeded` is the only one
   * the product raises today. */
  hold_reason: string;
  origin: string;
  external_system: string | null;
  /** Where an ERP's document number lands on the way back. */
  external_ref: string | null;
  sync_state: string;
}

export interface OrderPolicy {
  tax_rate_pct: string;
  shipping_flat: string;
  free_shipping_above: string | null;
  hold_over_credit_limit: boolean;
  payment_terms: string;
  reference_prefix: string;
  /** The quotation series, kept apart from the order series so that a
   * customer can tell a price from a commitment by its number alone. */
  quote_reference_prefix: string;
  /** How long a priced quotation stands, in days, unless the desk overrides
   * it when sending. */
  quote_validity_days: number;
}

export interface OrderQuery {
  clientId?: string | undefined;
  status?: OrderStatus | undefined;
  openOnly?: boolean | undefined;
  search?: string | undefined;
  afterId?: string | undefined;
  limit?: number | undefined;
  /** Orders only, quotations only, or — omitted — both. */
  kind?: OrderKind | undefined;
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

/** Every back-office order call, in one place. */
export class OrdersApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  list(options: OrderQuery = {}): Promise<OrderList> {
    return this.#client.request<OrderList>(
      `${BASE}/orders${query({
        client_id: options.clientId,
        status: options.status,
        open_only: options.openOnly,
        search: options.search,
        after_id: options.afterId,
        limit: options.limit,
        kind: options.kind,
      })}`,
    );
  }

  read(orderId: string): Promise<BackOfficeOrder> {
    return this.#client.request<BackOfficeOrder>(`${BASE}/orders/${encodeURIComponent(orderId)}`);
  }

  /** Release an order held for a credit overrun. Reserves stock for it. */
  approve(orderId: string): Promise<Order> {
    return this.#client.request<Order>(`${BASE}/orders/${encodeURIComponent(orderId)}/approve`, {
      method: "POST",
    });
  }

  cancel(orderId: string, reason: string): Promise<Order> {
    return this.#client.request<Order>(`${BASE}/orders/${encodeURIComponent(orderId)}/cancel`, {
      method: "POST",
      body: { reason },
    });
  }

  markPrepared(orderId: string, lineId: string, quantity: string): Promise<BackOfficeOrder> {
    return this.#client.request<BackOfficeOrder>(
      `${BASE}/orders/${encodeURIComponent(orderId)}/prepared`,
      { method: "POST", body: { line_id: lineId, quantity } },
    );
  }

  /** Record a departure. Partial by design — an order can have several. */
  ship(
    orderId: string,
    body: {
      lines: { line_id: string; quantity: string }[];
      carrier?: string;
      tracking_reference?: string;
    },
  ): Promise<BackOfficeOrder> {
    return this.#client.request<BackOfficeOrder>(
      `${BASE}/orders/${encodeURIComponent(orderId)}/shipments`,
      { method: "POST", body },
    );
  }

  /**
   * Price a quotation and put a date on it — the customer's "Devis à valider".
   *
   * The prices are the ones the quotation already carries: they came from the
   * customer's own tariff when it was requested, and re-resolving them here
   * would mean the customer was quoted whatever the tariff said on the day
   * somebody got round to it. What this adds is the deadline.
   */
  sendQuote(
    orderId: string,
    body: { validity_days?: number; comment?: string } = {},
  ): Promise<Order> {
    return this.#client.request<Order>(`${BASE}/orders/${encodeURIComponent(orderId)}/quote/send`, {
      method: "POST",
      body,
    });
  }

  /**
   * Say no to a request for a price.
   *
   * Its own call rather than `cancel`: cancelling releases reservations and
   * credits the balance back, and a quotation holds neither.
   */
  declineQuote(orderId: string, reason: string): Promise<Order> {
    return this.#client.request<Order>(
      `${BASE}/orders/${encodeURIComponent(orderId)}/quote/decline`,
      { method: "POST", body: { reason } },
    );
  }

  policy(): Promise<OrderPolicy> {
    return this.#client.request<OrderPolicy>(`${BASE}/order-policy`);
  }

  savePolicy(body: OrderPolicy): Promise<OrderPolicy> {
    return this.#client.request<OrderPolicy>(`${BASE}/order-policy`, {
      method: "PUT",
      body,
    });
  }
}
